<script>
  import { MAIL_DARK_ICON } from "@utils/icons.js";
  import CardColumn from "./CardColumn.svelte";
  import InvitationMessageModal from "./InvitationMessageModal.svelte";
  import SeamlessGridHeader from "./common/SeamlessGridHeader.svelte";
  import { BadgeColor, Button } from "paxray-components-svelte";

  export let resources = [];
  export let headerName = "";
  export let handleOpenSidePanel = null;
  export let currentSelection = null;
  export let handleAction = null;
  export let accentColor = "";
  export let tooltipText = "";

  let invitationOpen = false;
  let activeInvitation = null;

  function openInvitation(resource) {
    const link = resource.url
      ? new URL(resource.url, window.location.origin).href
      : null;
    activeInvitation = resource.invitation(link);
    invitationOpen = true;
  }
</script>

<div class="workshop_column">
  <SeamlessGridHeader
    header={headerName}
    indicatorColor={accentColor}
    {tooltipText}
    rightIndicatorValue={resources.length}
  />
  <div class="workshop_column_items">
    {#each resources as resource}
      <CardColumn
        onClick={() => handleOpenSidePanel?.(resource)}
        selected={currentSelection?.id === resource.id}
        minHeight="14rem"
        border
        style={"padding: 1rem;"}
        clickable={false}
      >
        <div class="resource_card_header">
          <div class="resource_icon">
            <img src="/img/assistant/icons/{resource.icon}.svg" alt="" />
          </div>
          <div class="resource_title_group">
            <h3>{resource.name}</h3>
            <div class="tag_row">
              <BadgeColor
                text={resource.type}
                color="grey"
                small={true}
              />
              {#if resource.status && !resource.unavailable}
                <BadgeColor
                  text={resource.status}
                  color="yellow"
                  small={true}
                />
              {/if}
              {#if resource.size}
                <BadgeColor
                  text={resource.size}
                  color="light-grey"
                  small={true}
                  border={true}
                  textColor="#6f7681"
                />
              {/if}
            </div>
          </div>
        </div>

        <p class="resource_description">{resource.description}</p>

        <footer class="resource_actions">
          {#if resource.unavailable}
            <Button
              kind="secondary"
              iconLeft
              icon={resource.actionIcon}
              text={resource.status}
              disabled
            />
          {:else}
            <Button
              kind="secondary"
              iconLeft
              icon={resource.actionIcon}
              text={resource.buttonText}
              on:click={() => handleAction(resource)}
              url={resource.file ? undefined : resource.url}
            />
          {/if}
          <!-- The suggested message refers to the resource, so only while it is available -->
          {#if resource.invitation && !resource.unavailable}
            <Button
              kind="outline-dark"
              iconLeft
              icon={MAIL_DARK_ICON}
              text={resource.invitationButtonText}
              on:click={() => openInvitation(resource)}
            />
          {/if}
        </footer>
      </CardColumn>
    {/each}
  </div>
</div>

<InvitationMessageModal
  bind:open={invitationOpen}
  invitation={activeInvitation}
/>

<style>
  .workshop_column {
    display: flex;
    min-height: 32rem;
    flex-direction: column;
    gap: 0;
    width: 100%;
    min-width: 0;
  }

  .workshop_column_items {
    display: flex;
    flex: 1;
    min-height: 0;
    flex-direction: column;
    gap: var(--control-gap);
    padding: 1rem;
  }

  :global(.card_actions .card_action_button Button span) {
    max-width: 10rem;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  :global(.card_action_button .bx--popover) {
    padding: 0.5rem 1.2rem;
  }

  :global(.card_action_button .bx--popover .bx--popover-contents) {
    padding: 0.5rem;
  }

  :global(.card_action_button .bx--popover::before) {
    top: 0;
    right: 0;
    left: 0;
    height: var(--cds-popover-offset);
    -webkit-transform: translateY(-100%);
    transform: translateY(-100%);
  }
  :global(.card_action_button .bx--popover .bx--popover-contents::before) {
    position: absolute;
    display: inline-block;
    width: 0.5rem;
    height: 0.5rem;
    background-color: inherit;
    content: "";
    z-index: -1;
    box-shadow: 2px 2px 6px 0 rgba(0, 0, 0, 0.2);
  }

  :global(.card_action_button .bx--popover .bx--popover-contents::after) {
    top: 0;
    left: 50%;
    transform: translate(-50%, -50%) rotate(45deg);
  }

  @keyframes fadeInOutWithDelay {
    0% {
      opacity: 0;
    }
    10% {
      opacity: 1;
    }
    90% {
      opacity: 1;
    }
    100% {
      opacity: 0;
    }
  }

  .resource_card_header {
    display: flex;
    gap: 0.875rem;
    align-items: stretch;
  }

  .resource_icon {
    width: 3.25rem;
    min-height: 3.25rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    border-radius: 0.5rem;
    background: var(--pax-blue);
    color: var(--pax-black);
  }

  .resource_icon img {
    width: 1.5rem;
    height: 1.5rem;
    display: block;
    filter: brightness(0);
  }

  .resource_title_group {
    min-width: 0;
  }

  .resource_title_group h3 {
    margin: 0;
    color: var(--pax-black);
    font-size: 0.925rem;
    font-weight: 700;
    line-height: 1.25;
  }

  .tag_row {
    display: flex;
    flex-wrap: wrap;
    gap: 0.35rem;
    margin-top: 0.45rem;
  }

  .resource_description {
    margin: 0.25rem 0 0;
    color: #4d5358;
    font-size: 0.875rem;
    line-height: 1.4;
  }

  .resource_actions {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-top: auto;
    padding-top: 0.5rem;
  }
</style>
